import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield,
  Building2,
  CheckCircle,
  ArrowRight,
  Users,
  FileText,
  BarChart3,
  Lock,
  Cloud,
  Zap,
  Globe,
  Award,
  TrendingUp,
  Server,
  Brain,
  Layers,
  ChevronRight,
  Star,
  Menu,
  X,
  Play,
  Clock,
  Target,
  ShieldCheck
} from 'lucide-react';

// Configuration object for easy editing of homepage content
const HOMEPAGE_CONFIG = {
  hero: {
    title: "Enterprise Risk & Compliance",
    subtitle: "Powered by AI",
    description: "Transform your risk assessment and compliance management with AI-driven insights, automated workflows, and enterprise-grade security.",
    ctaPrimary: "Start Free 14-Day Trial",
    ctaSecondary: "Watch Demo",
    backgroundImage: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072",
    dashboardImage: "https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070"
  },
  company: {
    name: "JARVIS RCSA",
    tagline: "Enterprise Risk & Compliance Management Platform",
    logoPath: "/images/jarvis_logo.png"
  },
  features: {
    enabled: true,
    sectionTitle: "Everything You Need for Risk & Compliance",
    sectionDescription: "Comprehensive features designed to streamline your risk assessment and compliance processes"
  },
  showcase: {
    enabled: true,
    title: "AI-Powered Risk Intelligence",
    description: "Harness the power of artificial intelligence to identify, assess, and mitigate risks before they impact your business. Our advanced AI engine analyzes patterns, predicts potential issues, and provides actionable recommendations.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015"
  },
  pricing: {
    enabled: true,
    sectionTitle: "Simple, Transparent Pricing",
    sectionDescription: "Choose the perfect plan for your organization's needs"
  },
  testimonials: {
    enabled: true,
    sectionTitle: "Trusted by Industry Leaders",
    sectionDescription: "See what our customers have to say about JARVIS RCSA"
  },
  security: {
    enabled: true,
    title: "Enterprise-Grade Security",
    description: "Your data security is our top priority. We employ industry-leading security measures to ensure your sensitive information is always protected.",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070"
  },
  cta: {
    title: "Ready to Transform Your Risk Management?",
    description: "Join thousands of organizations using JARVIS RCSA to streamline their compliance processes",
    buttonText: "Start Your Free Trial"
  },
  colors: {
    primary: "dusky-purple",
    secondary: "electric-blue",
    accent: "medium-purple"
  },
  footer: {
    enabled: true,
    copyrightYear: new Date().getFullYear()
  }
};

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [animateStats, setAnimateStats] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Trigger stats animation when they come into view
      const statsSection = document.getElementById('stats-section');
      if (statsSection) {
        const rect = statsSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setAnimateStats(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Powered Analysis",
      description: "Leverage advanced AI to automate risk assessment and compliance analysis with Claude integration"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enterprise Security",
      description: "Bank-grade security with SOC 2 compliance, end-to-end encryption, and MFA support"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-Tenant Architecture",
      description: "Isolated tenant environments with custom domains and white-labeling capabilities"
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Real-Time Analytics",
      description: "Comprehensive dashboards and reports for risk monitoring and compliance tracking"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Collaboration",
      description: "Role-based access control with seamless team collaboration features"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Automated Workflows",
      description: "Streamline processes with automated risk assessments and compliance workflows"
    }
  ];

  const plans = [
    {
      name: "Basic",
      price: "$49",
      period: "/month",
      description: "Perfect for small teams getting started",
      features: [
        "Up to 5 users",
        "10 projects",
        "1 GB storage",
        "Email support",
        "Basic analytics",
        "Standard templates"
      ],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Professional",
      price: "$199",
      period: "/month",
      description: "For growing organizations",
      features: [
        "Up to 50 users",
        "100 projects",
        "10 GB storage",
        "Priority support",
        "Advanced analytics",
        "API access",
        "Custom templates",
        "Export capabilities"
      ],
      cta: "Start Free Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large organizations with specific needs",
      features: [
        "Unlimited users",
        "Unlimited projects",
        "100+ GB storage",
        "Dedicated support",
        "Custom integrations",
        "White labeling",
        "SSO/SAML",
        "Audit logs",
        "SLA guarantee"
      ],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const testimonials = [
    {
      quote: "JARVIS RCSA has transformed how we handle risk assessments. The AI-powered analysis saves us hours of manual work.",
      author: "Sarah Chen",
      role: "Chief Risk Officer",
      company: "TechCorp Global",
      rating: 5
    },
    {
      quote: "The multi-tenant architecture allows us to manage multiple clients efficiently while maintaining complete data isolation.",
      author: "Michael Rodriguez",
      role: "Compliance Director",
      company: "FinanceHub Solutions",
      rating: 5
    },
    {
      quote: "Best-in-class security features and intuitive interface. Our team adopted it immediately with minimal training.",
      author: "Emily Thompson",
      role: "VP of Operations",
      company: "SecureNet Industries",
      rating: 5
    }
  ];

  const stats = [
    { value: "99.9%", label: "Uptime SLA" },
    { value: "500+", label: "Enterprise Clients" },
    { value: "2M+", label: "Risk Assessments" },
    { value: "24/7", label: "Support Available" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src={HOMEPAGE_CONFIG.company.logoPath}
                alt="Jarvis Logo"
                className="h-8 w-auto object-contain mr-2"
              />
              <span className="text-xl font-bold text-gray-900">{HOMEPAGE_CONFIG.company.name}</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-dusky-purple transition">Features</a>
              {/* <a href="#pricing" className="text-gray-700 hover:text-dusky-purple transition">Pricing</a> */}
              <a href="#testimonials" className="text-gray-700 hover:text-dusky-purple transition">Testimonials</a>
              {user ? (
                <>
                  <Link to="/app/dashboard" className="text-gray-700 hover:text-dusky-purple transition">Dashboard</Link>
                  <button 
                    onClick={handleLogout}
                    className="text-gray-700 hover:text-dusky-purple transition"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-dusky-purple transition">Login</Link>
                  <Link 
                    to="/signup"
                    className="bg-dusky-purple text-white px-4 py-2 rounded-lg hover:bg-super-dark-purple transition"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="#features" className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">Features</a>
              <a href="#pricing" className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">Pricing</a>
              <a href="#testimonials" className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">Testimonials</a>
              {user ? (
                <>
                  <Link to="/app/dashboard" className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">Dashboard</Link>
                  <button 
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">Login</Link>
                  <Link to="/signup" className="block px-3 py-2 bg-dusky-purple text-white rounded">Get Started</Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HOMEPAGE_CONFIG.hero.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {HOMEPAGE_CONFIG.hero.title}
              <span className="block text-dusky-purple bg-gradient-to-r from-dusky-purple to-medium-purple bg-clip-text text-transparent">{HOMEPAGE_CONFIG.hero.subtitle}</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {HOMEPAGE_CONFIG.hero.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              {user ? (
                <button
                  onClick={() => navigate('/app/dashboard')}
                  className="bg-gradient-to-r from-dusky-purple to-medium-purple text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/signup')}
                    className="bg-gradient-to-r from-dusky-purple to-medium-purple text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group"
                  >
                    {HOMEPAGE_CONFIG.hero.ctaPrimary}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button className="border-2 border-dusky-purple text-dusky-purple px-8 py-4 rounded-xl text-lg font-semibold hover:bg-dusky-purple hover:text-white transition-all duration-300 flex items-center justify-center group">
                    <Play className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                    {HOMEPAGE_CONFIG.hero.ctaSecondary}
                  </button>
                </>
              )}
            </div>

            <div className="flex justify-center space-x-8 text-sm text-gray-600">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                No credit card required
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                14-day free trial
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                Cancel anytime
              </div>
            </div>
          </div>
        </div>

        {/* Hero Image */}
        <div className="mt-16 relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-xl shadow-2xl overflow-hidden">
              <img
                src={HOMEPAGE_CONFIG.hero.dashboardImage}
                alt="Dashboard Preview"
                className="w-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section id="stats-section" className="py-20 bg-gradient-to-r from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className={`text-center transform hover:scale-105 transition-all duration-700 ${
                  animateStats ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-dusky-purple via-medium-purple to-light-purple bg-clip-text text-transparent animate-pulse">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Features Section */}
      {HOMEPAGE_CONFIG.features.enabled && (
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {HOMEPAGE_CONFIG.features.sectionTitle}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {HOMEPAGE_CONFIG.features.sectionDescription}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-2xl hover:scale-105 transition-all duration-300 group">
                <div className="w-16 h-16 bg-gradient-to-br from-dusky-purple/10 to-medium-purple/10 rounded-2xl flex items-center justify-center mb-6 text-dusky-purple group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-dusky-purple transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Product Showcase */}
      {HOMEPAGE_CONFIG.showcase.enabled && (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {HOMEPAGE_CONFIG.showcase.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {HOMEPAGE_CONFIG.showcase.description}
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">Automated Risk Scoring</h4>
                    <p className="text-gray-600">Real-time risk scoring based on multiple data points</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">Predictive Analytics</h4>
                    <p className="text-gray-600">Forecast potential compliance issues before they occur</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">Smart Recommendations</h4>
                    <p className="text-gray-600">AI-generated action plans for risk mitigation</p>
                  </div>
                </div>
              </div>
              
              <button className="mt-8 bg-electric-blue text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition flex items-center">
                Learn More
                <ChevronRight className="ml-2 w-5 h-5" />
              </button>
            </div>
            
            <div className="relative">
              <img
                src={HOMEPAGE_CONFIG.showcase.image}
                alt="AI Analytics Dashboard"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Risk Score</div>
                    <div className="text-2xl font-bold text-gray-900">92%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Pricing Section */}
      {/* {HOMEPAGE_CONFIG.pricing.enabled && (
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {HOMEPAGE_CONFIG.pricing.sectionTitle}
            </h2>
            <p className="text-xl text-gray-600">
              {HOMEPAGE_CONFIG.pricing.sectionDescription}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl ${
                  plan.popular ? 'ring-2 ring-dusky-purple shadow-2xl scale-105' : 'border border-gray-200 hover:shadow-xl'
                } p-8 transition-all duration-300`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-dusky-purple to-medium-purple text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold bg-gradient-to-r from-dusky-purple to-medium-purple bg-clip-text text-transparent">{plan.price}</span>
                    <span className="text-gray-600 ml-1">{plan.period}</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => navigate('/signup')}
                  className={`w-full py-4 rounded-xl font-semibold transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-dusky-purple to-medium-purple text-white hover:shadow-lg hover:scale-105'
                      : 'bg-gray-100 text-gray-900 hover:bg-dusky-purple hover:text-white'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
      )} */}

      {/* Testimonials */}
      {HOMEPAGE_CONFIG.testimonials.enabled && (
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {HOMEPAGE_CONFIG.testimonials.sectionTitle}
            </h2>
            <p className="text-xl text-gray-600">
              {HOMEPAGE_CONFIG.testimonials.sectionDescription}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-full mr-4" />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-sm text-gray-500">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Security Section */}
      {HOMEPAGE_CONFIG.security.enabled && (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={HOMEPAGE_CONFIG.security.image}
                alt="Security Infrastructure"
                className="rounded-xl shadow-2xl"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {HOMEPAGE_CONFIG.security.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {HOMEPAGE_CONFIG.security.description}
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start">
                  <Lock className="w-6 h-6 text-electric-blue mt-1" />
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900">SOC 2 Compliant</h4>
                    <p className="text-sm text-gray-600">Type II certified</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <ShieldCheck className="w-6 h-6 text-electric-blue mt-1" />
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900">End-to-End Encryption</h4>
                    <p className="text-sm text-gray-600">AES-256 encryption</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Server className="w-6 h-6 text-electric-blue mt-1" />
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900">99.9% Uptime</h4>
                    <p className="text-sm text-gray-600">SLA guaranteed</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Cloud className="w-6 h-6 text-electric-blue mt-1" />
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900">Cloud Backup</h4>
                    <p className="text-sm text-gray-600">Automated daily backups</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  ISO 27001
                </div>
                <div className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  GDPR Compliant
                </div>
                <div className="flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  HIPAA Ready
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-dusky-purple via-medium-purple to-light-purple relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-48 -translate-y-48"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-48 translate-y-48"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {HOMEPAGE_CONFIG.cta.title}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {HOMEPAGE_CONFIG.cta.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
            {user ? (
              <button
                onClick={() => navigate('/app/dashboard')}
                className="bg-white text-dusky-purple px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group"
              >
                Go to Dashboard
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-white text-dusky-purple px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center group"
                >
                  {HOMEPAGE_CONFIG.cta.buttonText}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-dusky-purple transition-all duration-300">
                  Schedule Demo
                </button>
              </>
            )}
          </div>
          
          <p className="mt-6 text-white/80 text-sm">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      {HOMEPAGE_CONFIG.footer.enabled && (
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src={HOMEPAGE_CONFIG.company.logoPath}
                  alt="Jarvis Logo"
                  className="h-8 w-auto object-contain mr-2"
                />
                <span className="text-xl font-bold">{HOMEPAGE_CONFIG.company.name}</span>
              </div>
              <p className="text-gray-400">
                {HOMEPAGE_CONFIG.company.tagline}
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                {/* <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li> */}
                <li><a href="#" className="hover:text-white transition">Security</a></li>
                <li><a href="#" className="hover:text-white transition">API Docs</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition">About Us</a></li>
                <li><a href="#" className="hover:text-white transition">Careers</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition">Security</a></li>
                <li><a href="#" className="hover:text-white transition">Compliance</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {HOMEPAGE_CONFIG.footer.copyrightYear} {HOMEPAGE_CONFIG.company.name} Platform. All rights reserved.</p>
          </div>
        </div>
      </footer>
      )}

      {/* Floating Action Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <button
          onClick={() => navigate(user ? '/app/dashboard' : '/signup')}
          className="bg-gradient-to-r from-dusky-purple to-medium-purple text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 group"
          title={user ? "Go to Dashboard" : "Start Free Trial"}
        >
          <div className="flex items-center">
            <Zap className="w-6 h-6 group-hover:animate-bounce" />
            <span className="ml-2 font-semibold hidden lg:block">
              {user ? "Dashboard" : "Start Free Trial"}
            </span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Landing;